import React, { useEffect } from 'react'

import { user } from 'ampersand-app'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router'
import { checkAccessRights, mapRouteToUserRight } from '@/modules'
import { ROUTES } from './modules/account/constants'
import { useAppSelector } from './redux/store'
import TimeoutLoader from '@/modules/base/components/loader/loader-with-timeout'
import { USER_RIGHTS, SUBSCRIPTION_MODULES, experimentalFeatures } from '@/modules/base/util/constants'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'

// upper bound for "waiting for login/access data"; afterwards the loader turns into an error panel
const ACCESS_TIMEOUT_MS = 30000

const ProtectedRoute = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const userInfo = useAppSelector(state => state.user.info)
  const documentModuleAccess = useAppSelector(state => state.permission.moduleAccess[SUBSCRIPTION_MODULES.DOCUMENTS])
  const routeRights = mapRouteToUserRight(location.pathname)
  const access = checkAccessRights(routeRights?.READ, userInfo)
  const outletContext = useOutletContext()
  const subscriptionLoading = useAppSelector(state => state.subscription.details.loading)
  // set when the user info request failed; without it roles stay empty and every route looks forbidden
  const userInfoError = useAppSelector(state => state.user.info.error)
  const isDocumentRoute = location.pathname.startsWith('/documents')
  const allowDocumentAccess = isDocumentRoute && Boolean(documentModuleAccess) && Boolean(userInfo?.isOwner)
  // FLAG:useDocuAi — the central review component sits under /documents/review/:id, but it is the
  // review surface of the BOOKKEEPING flow: the expense and revenue lists open it for their
  // review-pending rows (expense-list / revenue-list / expense-edit-page). Gating it like a
  // Documents page (documents module + isOwner) bounced every tenant that books but has no
  // Documents subscription straight to /dashboard — clicking a "Zu prüfen" expense did nothing
  // but navigate away. Grant it on the same modules its entry points already require instead.
  // Flag-OFF the whole term is false, so the guard stays byte-identical to development — and no
  // flag-OFF surface navigates here anyway (the page itself redirects flag-OFF tenants).
  const useDocuAiEnabled = Boolean(user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi))
  const isInvoiceReviewRoute = location.pathname.startsWith('/documents/review/')
  const expenseAccess = checkAccessRights(USER_RIGHTS.EXPENSES.READ, userInfo)
  const revenueAccess = checkAccessRights(USER_RIGHTS.REVENUES.READ, userInfo)
  const allowDocuAiInvoiceReview = Boolean(
    useDocuAiEnabled && isInvoiceReviewRoute && (expenseAccess || revenueAccess),
  )
  const hasAccess = Boolean(access || allowDocumentAccess || allowDocuAiInvoiceReview)
  const isDashboard = location.pathname === '/dashboard'

  useEffect(() => {
    if (subscriptionLoading && isDocumentRoute) {
      return
    }
    if (!user?.loggedIn && !user?.loginInProgress) {
      if (location.pathname.startsWith('/shop')) {
        WebStorageService.setItem(WebStorageKey.RETURN_AFTER_LOGIN, `${location.pathname}${location.search || ''}`)
      }
      navigate(ROUTES.login, { replace: true })
    } else if ((userInfo?.id || userInfoError) && !hasAccess && !isDashboard) {
      // the isDashboard guard prevents a redirect loop when the dashboard itself is not accessible
      navigate('/dashboard', { replace: true })
    }
  }, [user?.loggedIn, user?.loginInProgress, userInfo?.id, userInfoError, access, location.pathname, location.search])

  if (!user || !user?.loggedIn || user?.loginInProgress) {
    return <TimeoutLoader timeoutMs={ACCESS_TIMEOUT_MS} />
  }

  if (!hasAccess && !(subscriptionLoading && isDocumentRoute)) {
    // the access data will never arrive, so fail visibly instead of spinning forever
    return <TimeoutLoader timeoutMs={userInfoError ? 0 : ACCESS_TIMEOUT_MS} />
  }

  return (<Outlet context={outletContext} />)
}

export default ProtectedRoute
